import { type ReactNode, useEffect, useId, useRef } from "react";

export interface DialogShellProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly testId?: string;
  readonly initialFocus?: "first" | "none";
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function DialogShell(props: DialogShellProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!props.open) {
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel && props.initialFocus !== "none") {
      const focusable = panel.querySelector<HTMLElement>(FOCUSABLE);
      focusable?.focus();
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
        return;
      }
      if (event.key !== "Tab" || !panel) {
        return;
      }
      const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
      );
      if (nodes.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [props.open, props.onClose, props.initialFocus]);

  if (!props.open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-3 sm:items-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Закрыть диалог"
        onClick={props.onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={props.testId ?? "dialog"}
        className="relative flex max-h-[min(90vh,900px)] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-pad-200 bg-white shadow-xl"
      >
        <header className="flex items-center justify-between gap-3 border-pad-200 border-b px-5 py-3.5">
          <h2 id={titleId} className="pwz-heading min-w-0 break-words text-lg">
            {props.title}
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            className="pwz-btn pwz-btn-ghost shrink-0"
            aria-label="Закрыть диалог"
            data-testid="dialog-close"
          >
            Закрыть
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 py-4">
          {props.children}
        </div>
        {props.footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-pad-200 border-t bg-pad-50 px-5 py-3.5">
            {props.footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
